import { useState } from 'react';
import { Check, Send, Loader2 } from 'lucide-react';
import { type PublishChannel } from '@/services/data/publishContent';
import { getChannelIcon } from '@/utils/channels';
import type { Channel } from '@/services/business/calendar';

interface SocialConnection {
  id: string;
  user_id: string;
  platform: string;
  connected: boolean;
  username: string | null;
}

interface CrossPostMenuProps {
  readonly platforms: SocialConnection[];
  readonly isPublishing: boolean;
  readonly onPublish: (channels: PublishChannel[]) => void;
  readonly onClose: () => void;
}

const VIDEO_COMPATIBLE_PLATFORMS = new Set(['instagram', 'tiktok', 'facebook', 'linkedin', 'youtube', 'threads']);

export function CrossPostMenu({ platforms, isPublishing, onPublish, onClose }: Readonly<CrossPostMenuProps>) {
  const [selectedChannels, setSelectedChannels] = useState<Set<PublishChannel>>(new Set());

  const videoCompatiblePlatforms = platforms.filter(
    p => p.connected && VIDEO_COMPATIBLE_PLATFORMS.has(p.platform.toLowerCase())
  );

  const toggleChannel = (channel: PublishChannel) => {
    const newSelected = new Set(selectedChannels);
    if (newSelected.has(channel)) {
      newSelected.delete(channel);
    } else {
      newSelected.add(channel);
    }
    setSelectedChannels(newSelected);
  };

  const selectAll = () => {
    const allChannels = new Set(
      videoCompatiblePlatforms.map(p => p.platform.toLowerCase() as PublishChannel)
    );
    setSelectedChannels(allChannels);
  };

  const handlePublish = () => {
    if (selectedChannels.size > 0) {
      onPublish(Array.from(selectedChannels));
    }
  };

  const getButtonText = () => {
    if (selectedChannels.size === 0) {
      return 'Sélectionner';
    }
    if (selectedChannels.size === 1) {
      return 'Publier';
    }
    return `Publier (${selectedChannels.size})`;
  };

  return (
    <div className="absolute bottom-full mb-2 right-0 bg-black/95 border border-white/10 rounded-lg py-3 min-w-[220px] shadow-xl z-50">
      <div className="px-3 pb-2 border-b border-white/10 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-white/40">
          Cross-post
        </p>
        <button
          onClick={selectAll}
          className="text-[10px] text-amber-400 hover:text-amber-300 transition-colors"
        >
          Tout sélectionner
        </button>
      </div>
      
      <div className="py-2 space-y-1">
        {videoCompatiblePlatforms.map(platform => {
          const channel = platform.platform.toLowerCase() as PublishChannel;
          const ChannelIcon = getChannelIcon(channel as Channel);
          const isSelected = selectedChannels.has(channel);
          
          return (
            <button
              key={platform.id}
              onClick={() => toggleChannel(channel)}
              className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-white/5 transition-colors ${
                isSelected ? 'bg-white/5' : ''
              }`}
            >
              <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${
                isSelected 
                  ? 'bg-amber-500 border-amber-500' 
                  : 'border-white/20'
              }`}>
                {isSelected && <Check className="h-3 w-3 text-white" />}
              </div>
              <ChannelIcon className="h-4 w-4 text-white/70" />
              <span className="text-sm text-white/80 capitalize flex-1 text-left">
                {platform.platform}
              </span>
            </button>
          );
        })}
      </div>

      <div className="px-3 pt-2 border-t border-white/10 flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 py-2 px-3 text-xs text-white/60 hover:text-white/80 transition-colors rounded-lg hover:bg-white/5"
        >
          Annuler
        </button>
        <button
          onClick={handlePublish}
          disabled={selectedChannels.size === 0 || isPublishing}
          className="flex-1 py-2 px-3 text-xs bg-amber-500 text-black rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isPublishing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <Send className="h-3 w-3" />
              <span>{getButtonText()}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
